import CloseIcon from '@mui/icons-material/Close';
import { Button, Grid, Link, List, ListItem, ListItemIcon, ListItemText, Modal, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createAutoService } from '../../../api/createServiceApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { updateClaimCvpRefNumState } from '../../../app/store/ClaimDetails/ClaimDetailSlice';
import {
  getAuthKey,
  getClaimNumber,
  getCvpEligible,
  getCvpRefNumber,
  getInsuredLastName,
  getPhotoUploadCCCFlag,
  getSessionId,
  getUrlKey,
} from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import { ClaimServicesConstants, goToClaimsBtn, skipPhotosBtn, uploadPhotosBtn } from '../../../Constants/Constants';
import { CreateAutoServiceRequest } from '../../../model/create-auto-service-request';
import { CreateServiceResponse, Features } from '../../../types/type';
import { getNextView, getStagesInOrder } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';
import {
  CameraIcon,
  CarDriverBackIcon,
  CarDriverFrontIcon,
  CarPassengerBackIcon,
  CarPassengerFrontIcon,
  DamageIcon,
  FigmaVinLocationsIcon,
  OdometerIcon,
  VinIcon,
} from '../../shared/Svg-icons/App-Icons';

export default function PhotoServicesHome(props: any) {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [vinModalOpen, setVinModalOpen] = React.useState(false);
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const cvpRefNum = useSelector(getCvpRefNumber);
  const insuredLastName = useSelector(getInsuredLastName);
  const cvpEligible = useSelector(getCvpEligible);
  const vin = useAppSelector((state) => _get(state, 'claimDetail.claimSummary.autoClaimDetails[0].vin', ''));
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const photoUploadCCC_Flag = useSelector(getPhotoUploadCCCFlag);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== Features.PHOTOS && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];
  const repairComplete = availableProgressStages.some((stage) => stage.id === Features.REPAIR && !!stage.completed);

  const headerContent = repairComplete
    ? 'Your repair shop will take photos of the damage, but you can submit your own photos as well.'
    : 'Have photos of the damage? Upload them now.';
  const bodyContent = repairComplete
    ? "If you don't have photos now, don't worry - you can add them later on the Claim Details screen. Please only upload .jpg files that are 10MB or less."
    : "Uploading photos will help us process your claim faster. If you don't have photos now, don't worry - you can add them later from the Claim Details page. Please only upload .jpg files that are 10MB or less.";

  async function handleNext() {
    if (photoUploadCCC_Flag) {
      if (cvpRefNum) {
        navigate('/photos-selection', {
          state: {
            isStandalone: false,
            cvpRefNum: cvpRefNum,
            insuredLastName: insuredLastName,
            authKey,
          },
        });
      } else if (cvpEligible) {
        const response = await selectService();

        // The claim may have become CVP-ineligible since eligibility was last fetched (e.g. a
        // DRP shop was set up), in which case the CVP service create call returns no ref number.
        // Fall back to the standalone Nuxeo upload rather than leaving the user stuck on this
        // screen with no feedback.
        if (!response?.cvpRefNum) {
          console.error(
            'Failed to create CVP service - invalid response, falling back to standalone upload:',
            response
          );
          navigate('/photos-selection', {
            state: {
              isStandalone: true,
              authKey: response?.updatedToken || authKey,
            },
          });
          return;
        }

        dispatch(
          updateClaimCvpRefNumState({
            cvpRefNum: response.cvpRefNum,
          })
        );
        navigate('/photos-selection', {
          state: {
            isStandalone: false,
            cvpRefNum: response.cvpRefNum,
            insuredLastName: insuredLastName,
            authKey: response.updatedToken,
          },
        });
      } else {
        navigate('/photos-selection', {
          state: {
            isStandalone: true,
            authKey,
          },
        });
      }
    } else {
      navigate('/photos-selection', {
        state: {
          isStandalone: true,
          authKey,
        },
      });
    }
  }
  async function selectService() {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    let cvpRefNum = '';
    let updatedToken = '';
    const postBody: CreateAutoServiceRequest = {
      claim: {
        claimNumber: claimNumber,
        vehicle: {
          vin: vin,
          serviceRequestInfo: {
            serviceRequestProviderId: '1001',
            serviceRequestCode: ClaimServicesConstants.CVP,
            serviceRequestDescription: ClaimServicesConstants.CVP,
          },
        },
      },
    };
    const response: CreateServiceResponse = await createAutoService(urlKey, sessionId, authKey, postBody);
    if ((_get(response, 'response.status.code') as unknown as number) === 200) {
      if (_get(response, 'response.updatedAuthToken')) {
        updatedToken = response.response.updatedAuthToken;
        contextRouterActions.updateAuthToken(updatedToken);
      }
      const fullRef = _get(response, 'response.vehicle[0].eligibleServices[0].serviceProviderId', '').split('#');
      cvpRefNum = fullRef.length === 2 ? fullRef[1] : fullRef[0];
    }
    // Close modal after API call completes (success or failure)
    setModalLoadingOpen(false);
    return { cvpRefNum, updatedToken };
  }

  function handleSkip() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.PHOTOS
          ? {
              ...stage,
              completed: false,
              viewActive: false,
              showProgress: true,
              skipped: true,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }

  const vinModal = (
    <Modal open={vinModalOpen}>
      <Box
        sx={{
          position: 'absolute',
          backgroundColor: theme.custom.colors.bodyColor,
          borderRadius: theme.custom.styles.borderRadius.radiusModal,
          boxShadow: theme.shadows[5],
          padding: '1rem',
          left: '50%',
          top: isMobile ? '21%' : '32%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : '488px',
        }}
      >
        <Grid container>
          <Grid container item>
            <Grid item xs={10}>
              <Typography
                sx={{
                  ...theme.custom.styles.modalHeader,
                }}
              >
                Where can I find my Vehicle Identification Number (VIN)?
              </Typography>
            </Grid>
            <Grid item sx={{ display: 'flex', justifyContent: 'flex-end' }} xs={2}>
              <Button
                startIcon={<CloseIcon />}
                onClick={() => setVinModalOpen(false)}
                sx={{
                  minWidth: 'auto',
                  px: '0px',
                  py: '0px',
                  alignItems: 'baseline',
                  color: theme.custom.styles.modalHeader.closeIconColor,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {theme.custom.styles.underLineModalHeader && <hr style={theme.custom.styles.modalHeaderHrStyle}></hr>}
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '2rem' }}>
            <FigmaVinLocationsIcon theme={theme}></FigmaVinLocationsIcon>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '2rem' }}>
            <Typography variant="body1">
              You may be able to find your VIN on the driver side door jamb, the interior of the driver side of your
              dashboard, near your windshield-washing unit, and under your hood on the front of your engine.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {vinModalOpen && vinModal}

      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      <PropertyServiceInfo
        headerContent={headerContent}
        infoContent={
          <>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                {bodyContent}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                You will be asked to upload at least 9 photos in the following categories:
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <List>
                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <VinIcon></VinIcon>
                  </ListItemIcon>
                  <ListItemText>
                    Your vehicle's vehicle identification number (VIN){' '}
                    <Link
                      component="button"
                      variant="body1"
                      underline="hover"
                      onClick={() => {
                        setVinModalOpen(true);
                      }}
                    >
                      Where do I find this?
                    </Link>
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <OdometerIcon></OdometerIcon>
                  </ListItemIcon>
                  <ListItemText>Your vehicle's mileage (odometer)</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <CarDriverFrontIcon></CarDriverFrontIcon>
                  </ListItemIcon>
                  <ListItemText>The front driver side of your vehicle</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <CarPassengerFrontIcon></CarPassengerFrontIcon>
                  </ListItemIcon>
                  <ListItemText>The front passenger side of your vehicle</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <CarDriverBackIcon></CarDriverBackIcon>
                  </ListItemIcon>
                  <ListItemText>The back of your driver side of your vehicle</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <CarPassengerBackIcon></CarPassengerBackIcon>
                  </ListItemIcon>
                  <ListItemText>The back passenger side of your vehicle</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DamageIcon></DamageIcon>
                  </ListItemIcon>
                  <ListItemText>The center of your vehicle's damage</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DamageIcon></DamageIcon>
                  </ListItemIcon>
                  <ListItemText>The left side of your vehicle's damage</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <DamageIcon></DamageIcon>
                  </ListItemIcon>
                  <ListItemText>The right side of your vehicle's damage</ListItemText>
                </ListItem>

                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <CameraIcon></CameraIcon>
                  </ListItemIcon>
                  <ListItemText>Optional, any other photos you'd like to include</ListItemText>
                </ListItem>
              </List>
            </Grid>
          </>
        }
      ></PropertyServiceInfo>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <CallToAction
            nextButton={uploadPhotosBtn}
            nextButtonContent={uploadPhotosBtn}
            handleNext={() => handleNext()}
            backButton={false}
            skipButton={skipPhotosBtn}
            skipButtonContent={skipPhotosBtn}
            handleSkip={() => handleSkip()}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
